import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import drawManager from '../lessonPlayer/managers/DrawManager';

const COLLAPSED_WIDTH = 48;
const EXPANDED_MIN_WIDTH = 180;
const EXPANDED_DEFAULT_WIDTH = 180;
const TWO_COLUMN_MIN_WIDTH = 280;
const MAX_WIDTH = 336;
const DOCK_GAP_FROM_DRAW = 10;

/** Outermost question shells used as drag bounds and start clamping. */
const QUESTION_BOUNDS_SELECTOR = '.draw-wrapper-container, .draw-overlay-question, .stand-alone-wrapper';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getQuestionBoundsEl = (fromEl) => (
  fromEl?.closest(QUESTION_BOUNDS_SELECTOR)
  || fromEl?.closest('.lesson-element')
  || fromEl
);

const getQuestionClientRect = (el) => {
  if (!el) {
    return { left: 0, top: 0, right: 0, bottom: 0 };
  }
  const { left, top, right, bottom } = el.getBoundingClientRect();
  return { left, top, right, bottom };
};

/** Clamp anchor-relative x/y to the question outermost div.
 * Vertical bottom limit uses the drag-bar (header) bottom, not the full menu. */
const clampToQuestionBounds = (x, y, toolbar, anchor) => {
  if (!toolbar || !anchor) {
    return { x, y };
  }

  const bounds = getQuestionClientRect(getQuestionBoundsEl(anchor));
  const anchorRect = anchor.getBoundingClientRect();
  const toolbarWidth = toolbar.offsetWidth || EXPANDED_DEFAULT_WIDTH;
  const dragBar = toolbar.querySelector('.draw-floating-toolbar-header');
  const dragBarHeight = dragBar?.offsetHeight || 36;

  const minX = bounds.left - anchorRect.left;
  const minY = bounds.top - anchorRect.top;
  const maxX = bounds.right - anchorRect.left - toolbarWidth;
  const maxY = bounds.bottom - anchorRect.top - dragBarHeight;

  return {
    x: clamp(x, minX, Math.max(minX, maxX)),
    y: clamp(y, minY, Math.max(minY, maxY)),
  };
};

/**
 * Start beside the draw area (1px anchor = draw width/left/top).
 * Top stays on the draw anchor (y = 0). Do not snap to the question parent
 * top — that sits above directions and was pulling the toolbar up.
 * Only clamp horizontal into the parent; nudge y down only if the anchor
 * itself is above the parent top.
 */
const computeStartPosition = (toolbar, anchor) => {
  const toolbarWidth = toolbar.offsetWidth || EXPANDED_DEFAULT_WIDTH;
  const bounds = getQuestionClientRect(getQuestionBoundsEl(anchor));
  const anchorRect = anchor.getBoundingClientRect();

  let x = -toolbarWidth - DOCK_GAP_FROM_DRAW;
  let y = 0;

  const minX = bounds.left - anchorRect.left;
  const maxX = bounds.right - anchorRect.left - toolbarWidth;
  x = clamp(x, minX, Math.max(minX, maxX));

  const minY = bounds.top - anchorRect.top;
  if (y < minY) {
    y = minY;
  }

  return { x, y };
};

const layoutIsReady = (anchor) => {
  const parent = getQuestionBoundsEl(anchor);
  if (!parent || !anchor) {
    return false;
  }
  const parentRect = parent.getBoundingClientRect();
  const anchorRect = anchor.getBoundingClientRect();
  if (parentRect.width <= 0 || anchorRect.width <= 0) {
    return false;
  }
  // Wait until the draw surface under the anchor has real height so we don't
  // place against a short parent (directions only) and stick above the stage.
  const surface = anchor.parentElement?.querySelector(
    '.draw-overlay-stage-inner, .drawBody, .canvasContainerOuter',
  );
  if (surface) {
    return surface.getBoundingClientRect().height > 0;
  }
  return true;
};

/**
 * Draw toolbar chrome (collapse / resize / drag).
 *
 * After layout is ready, measures the question outermost div and the 1px
 * draw-width anchor, then sets left/top start state. Drag stays absolute
 * relative to the anchor and clamped to that parent div.
 */
const useDrawFloatingToolbar = (lessonElementId) => {
  const toolbarRef = useRef(null);
  const dragState = useRef(null);
  const resizeState = useRef(null);
  const startInitializedRef = useRef(false);
  const hasDraggedRef = useRef(false);

  const saved = drawManager.getToolbarUi(lessonElementId);

  const [position, setPosition] = useState(null);
  const [width, setWidth] = useState(() => saved?.width || EXPANDED_DEFAULT_WIDTH);
  const [collapsed, setCollapsed] = useState(() => !!saved?.collapsed);

  const effectiveWidth = collapsed ? COLLAPSED_WIDTH : width;
  const columns = (!collapsed && width >= TWO_COLUMN_MIN_WIDTH) ? 2 : 1;
  const isPlaced = position != null;

  const persist = useCallback((next) => {
    drawManager.setToolbarUi(lessonElementId, next);
  }, [lessonElementId]);

  const prevLessonElementIdRef = useRef(lessonElementId);

  // After render/layout: read parent + anchor bounds, then set start left/top.
  useLayoutEffect(() => {
    if (prevLessonElementIdRef.current !== lessonElementId) {
      prevLessonElementIdRef.current = lessonElementId;
      startInitializedRef.current = false;
      hasDraggedRef.current = false;
      setPosition(null);
    }

    const tryPlace = () => {
      const toolbar = toolbarRef.current;
      const anchor = toolbar?.parentElement;
      if (!toolbar || !anchor || !layoutIsReady(anchor)) {
        return false;
      }

      const savedUi = drawManager.getToolbarUi(lessonElementId);
      let next;
      if (savedUi?.floating || hasDraggedRef.current) {
        const x = savedUi?.floating ? savedUi.x : (position?.x ?? 0);
        const y = savedUi?.floating ? savedUi.y : (position?.y ?? 0);
        next = clampToQuestionBounds(x, y, toolbar, anchor);
      } else {
        next = computeStartPosition(toolbar, anchor);
      }

      startInitializedRef.current = true;
      setPosition((prev) => (
        prev && prev.x === next.x && prev.y === next.y ? prev : next
      ));
      return true;
    };

    const onLayoutChange = () => {
      const currentToolbar = toolbarRef.current;
      const currentAnchor = currentToolbar?.parentElement;
      if (!currentToolbar || !currentAnchor || !layoutIsReady(currentAnchor)) {
        return;
      }

      const savedUi = drawManager.getToolbarUi(lessonElementId);

      // Until the user drags, keep re-measuring dock start as directions/stage settle.
      if (!hasDraggedRef.current && !savedUi?.floating) {
        const docked = computeStartPosition(currentToolbar, currentAnchor);
        startInitializedRef.current = true;
        setPosition((prev) => (
          prev && prev.x === docked.x && prev.y === docked.y ? prev : docked
        ));
        return;
      }

      if (!startInitializedRef.current) {
        tryPlace();
        return;
      }

      setPosition((prev) => {
        if (!prev) {
          return prev;
        }
        const next = clampToQuestionBounds(prev.x, prev.y, currentToolbar, currentAnchor);
        return (next.x === prev.x && next.y === prev.y) ? prev : next;
      });
    };

    if (!startInitializedRef.current) {
      tryPlace();
    } else {
      onLayoutChange();
    }

    const toolbar = toolbarRef.current;
    const anchor = toolbar?.parentElement;
    const parent = anchor ? getQuestionBoundsEl(anchor) : null;
    const surface = anchor?.parentElement?.querySelector(
      '.draw-overlay-stage-inner, .drawBody, .canvasContainerOuter',
    );

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(onLayoutChange)
      : null;
    if (parent) {
      observer?.observe(parent);
    }
    if (anchor) {
      observer?.observe(anchor);
    }
    if (surface) {
      observer?.observe(surface);
    }
    if (toolbar) {
      observer?.observe(toolbar);
    }
    window.addEventListener('resize', onLayoutChange);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', onLayoutChange);
    };
  }, [lessonElementId, effectiveWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDragStart = useCallback((e) => {
    if (e.button !== 0 || !position) {
      return;
    }
    e.preventDefault();

    const toolbar = toolbarRef.current;
    const anchor = toolbar?.parentElement;
    if (!toolbar || !anchor) {
      return;
    }

    const clamped = clampToQuestionBounds(position.x, position.y, toolbar, anchor);
    if (clamped.x !== position.x || clamped.y !== position.y) {
      setPosition(clamped);
    }

    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: clamped.x,
      originY: clamped.y,
    };

    const onMove = (moveEvent) => {
      if (!dragState.current || !toolbarRef.current) {
        return;
      }
      const currentAnchor = toolbarRef.current.parentElement;
      const dx = moveEvent.clientX - dragState.current.startX;
      const dy = moveEvent.clientY - dragState.current.startY;
      setPosition(clampToQuestionBounds(
        dragState.current.originX + dx,
        dragState.current.originY + dy,
        toolbarRef.current,
        currentAnchor,
      ));
    };

    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      hasDraggedRef.current = true;
      setPosition((prev) => {
        if (!prev) {
          return prev;
        }
        persist({
          floating: true,
          x: prev.x,
          y: prev.y,
          width,
          collapsed,
        });
        return prev;
      });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [position, persist, width, collapsed]);

  const onResizeStart = useCallback((e) => {
    if (e.button !== 0 || collapsed) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = { startX: e.clientX, startWidth: width };

    const onMove = (moveEvent) => {
      if (!resizeState.current) {
        return;
      }
      const dx = moveEvent.clientX - resizeState.current.startX;
      setWidth(clamp(
        resizeState.current.startWidth + dx,
        EXPANDED_MIN_WIDTH,
        MAX_WIDTH,
      ));
    };

    const onUp = () => {
      resizeState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setWidth((nextWidth) => {
        const toolbar = toolbarRef.current;
        const anchor = toolbar?.parentElement;
        const savedUi = drawManager.getToolbarUi(lessonElementId);
        if (toolbar && anchor && position) {
          const nextPos = clampToQuestionBounds(position.x, position.y, toolbar, anchor);
          setPosition(nextPos);
          if (savedUi?.floating) {
            persist({
              floating: true,
              x: nextPos.x,
              y: nextPos.y,
              width: nextWidth,
              collapsed,
            });
          } else {
            persist({
              floating: false,
              x: nextPos.x,
              y: nextPos.y,
              width: nextWidth,
              collapsed,
            });
          }
        } else {
          persist({
            ...(savedUi || {}),
            width: nextWidth,
            collapsed,
          });
        }
        return nextWidth;
      });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [collapsed, width, lessonElementId, persist, position]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      const savedUi = drawManager.getToolbarUi(lessonElementId);
      if (savedUi?.floating && position) {
        persist({
          floating: true,
          x: position.x,
          y: position.y,
          width,
          collapsed: next,
        });
      }
      return next;
    });
  }, [lessonElementId, persist, position, width]);

  return {
    toolbarRef,
    isPlaced,
    position,
    effectiveWidth,
    columns,
    collapsed,
    toggleCollapsed,
    onDragStart,
    onResizeStart,
  };
};

export default useDrawFloatingToolbar;
